'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, RichTextField, asText } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import Image from 'next/image';
import { StarIcon } from '../../assets';

/**
 * Props for `TestimonialHighlight`.
 */
export type TestimonialHighlightProps =
  SliceComponentProps<Content.TestimonialHighlightSlice>;

/**
 * Component for "TestimonialHighlight" Slices.
 */
const TestimonialHighlight: FC<TestimonialHighlightProps> = ({ slice }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Prismic fields
  const title = slice.primary.title;
  const description = slice.primary.description;
  const testimonials = slice.primary.testimonials;

  // Use first testimonial in group
  const testimonial = testimonials?.[0];
  const rating = testimonial?.rating || 5;
  const quote = testimonial?.quote;
  const authorName = testimonial?.author_name;
  const authorRole = testimonial?.author_role;
  const authorAvatar = testimonial?.author_avatar;

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    if (cardRef.current) {
      const element = cardRef.current;
      element.classList.add(
        'opacity-0',
        'translate-y-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      observer.observe(element);

      return () => {
        observer.unobserve(element);
      };
    }
  }, []);

  // Helper to render Prismic rich text
  const renderText = (field: RichTextField | undefined): string => {
    return field ? asText(field) : '';
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-16 bg-gradient-to-b from-[#0E1010] to-[#0F423A]"
    >
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-white text-2xl md:text-3xl font-semibold mb-2">
          {renderText(title) || 'Voices from Our Team'}
        </h2>
        <p className="text-white/80 text-base md:text-lg mb-8">
          {renderText(description) ||
            'Check out what our Devsunians have to say about their experiences working with us.'}
        </p>
        <div
          ref={cardRef}
          className="flex flex-col md:flex-row items-center gap-6 w-full p-6 md:p-8 rounded-[16px] bg-white/10 shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)]"
        >
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex gap-1 mb-3">
              {[...Array(Number(rating))].map((_, i) => (
                <StarIcon key={i} fill="#FFD600" className="w-5 h-5" />
              ))}
            </div>
            <p className="text-white text-base md:text-lg font-light mb-4">
              {renderText(quote) ||
                '"I love the close-knit environment and the opportunities for professional growth that the company offers. It all depends on the desire you put into it, and that motivates me to make an effort every day."'}
            </p>
            <div>
              <span className="text-white font-semibold">
                {authorName || 'Nickolas Rodriguez'}
              </span>
              <br />
              <span className="text-white text-sm">
                {authorRole || 'Training specialist'}
              </span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-4 border-white/10">
              {authorAvatar?.url ? (
                <Image
                  src={authorAvatar.url}
                  alt={authorAvatar.alt || authorName || 'Testimonial'}
                  width={96}
                  height={96}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className="w-full h-full bg-gray-400 rounded-full" />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialHighlight;
